import React, { useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Upload, Button, Row, Col, Checkbox } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import SmartMentionsInput from './SmartMentionsInput';

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

const statusOptions = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

export default function TaskCreateModal({
  open,
  onCancel,
  form,
  onFinish,
  users = [],
  projects = [],
  tasks = [],
  parentTask,
  taskTypeOptions = [],
  priorityOptions = [],
  user,
  confirmLoading = false
}) {
  const [requiresValidation, setRequiresValidation] = useState(false);

  return (
    <Modal 
      title={parentTask ? `Новая подзадача для: ${parentTask.title}` : "Новая задача"} 
      open={open} 
      onCancel={onCancel} 
      onOk={() => form.submit()} 
      okText="Создать"
      confirmLoading={confirmLoading}
      width={{ xs: '100%', sm: '80%', md: 800 }}
      style={{ top: 20 }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="title" label="Название" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Описание">
          <SmartMentionsInput rows={3} />
        </Form.Item>
        
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="project_id" label="Проект" rules={[{ required: true }]}>
              <Select 
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} 
                options={projects.map(p => ({ value: p.id, label: p.name }))}
                placeholder="Выберите проект"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="task_type" label="Тип задачи" rules={[{ required: true }]} initialValue="coordination">
              <Select options={taskTypeOptions} />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item>
              <Checkbox 
                checked={requiresValidation}
                onChange={(e) => {
                  setRequiresValidation(e.target.checked);
                  if (!e.target.checked) {
                    form.setFieldsValue({ validator_id: undefined });
                  }
                }}
              >
                Требуется валидация
              </Checkbox>
            </Form.Item>
          </Col>
          {requiresValidation && (
            <Col xs={24} md={8}>
              <Form.Item name="validator_id" label="Валидатор" rules={[{ required: true, message: 'Выберите валидатора' }]}>
                <Select 
                  showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} 
                  options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                  placeholder="Выберите валидатора"
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={8}>
            <Form.Item name="curator_id" label="Куратор" rules={[{ required: true, message: 'Выберите куратора' }]}>
              <Select 
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} 
                options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                placeholder="Выберите куратора"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="assignee_id" label="Исполнитель">
              <Select 
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} 
                options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                placeholder="Выберите исполнителя"
              />
            </Form.Item>
          </Col>
        </Row>
        
         <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="status" label="Статус" initialValue="new" hidden>
              <Select options={statusOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="priority" label="Приоритет" initialValue="medium" hidden>
              <Select options={priorityOptions} />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="start_date" label="Дата начала">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="due_date" label="Срок выполнения">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="crash_date" label="Дата краха">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="estimated_hours" label="Оценка (часы)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="budget" label="Бюджет">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item name="watchers" label="Наблюдатели">
          <Select 
            mode="multiple"
            showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
            placeholder="Выберите наблюдателей"
          />
        </Form.Item>
        
        <Form.Item
          name="files"
          label="Прикрепленные файлы"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Upload
            beforeUpload={() => false}
            multiple={true}
          >
            <Button icon={<UploadOutlined />}>Выберите файлы</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
}
